import gql from 'graphql-tag';

import { MEDIAREF } from './AssetLibrary';
import { PUBLIC_SET_FRAGMENT, SET_PREVIEW_INFO } from './EWebinar';
import {
  REGISTRATION_FORM_FIELD_FRAGMENT,
  REGISTRATION_FORM_SETTING_FRAGMENT,
  REGISTRATION_PAGE_SETTINGS_FRAGMENT,
} from './PublicEWebinarFragment';
import { SCRIPT_IN_SERIES_FRAGMENT } from './Script';
import { TEAM_SETTINGS_FRAGMENT } from './TeamFragment';
import { THEME_SETTINGS_FRAGMENT } from './ThemeSettings';

export const SERIES_SETTINGS_FRAGMENT = gql`
  fragment SeriesSettings on SeriesSettings {
    requireSequential
    showSeriesProgressBar
    enableSeriesCertification
    showCountdownTimer
    completionRequirement
    certificationRewardType
    certificationBadge {
      ...MediaRef
    }
    certificationUrl
  }
  ${MEDIAREF}
`;

export const SUBMIT_EMAIL_FORM_SETTINGS_FRAGMENT = gql`
  fragment SubmitEmailFormSettings on SubmitEmailFormSettings {
    title
    submitButtonText
    fields {
      ...RegistrationFormField
    }
  }
  ${REGISTRATION_FORM_FIELD_FRAGMENT}
`;

export const NOTIFICATION_SETTINGS_FRAGMENT = gql`
  fragment NotificationSettings on NotificationSettings {
    fromName
    fromEmail
  }
`;

export const SERIES_SCRIPT_RELATION_FRAGMENT = gql`
  fragment SeriesScriptRelation on SeriesScriptRelation {
    id
    script {
      id
      name
      content
    }
    useInLandingPage
    useInRegistrationForm
    useInSubmitEmailForm
  }
`;

export const SERIES_FRAGMENT = gql`
  fragment Series on Series {
    id
    name
    language
    tag
    url
    updatedAt
    team {
      id
    }
    sets {
      id
      tags
    }
  }
`;

export const FULL_SERIES_FRAGMENT = gql`
  fragment FullSeries on Series {
    id
    name
    language
    tag
    url
    updatedAt
    team {
      id
      logoMediaRef {
        ...MediaRef
      }
      name
      shareUrl
      settings {
        ...TeamSettings
      }
    }
    logoMediaRef {
      ...MediaRef
    }
    favIconMediaRef {
      ...MediaRef
    }
    sets {
      ...SetPreviewInfo
    }
    useScripts {
      ...ScriptInSeries
    }
    registrationPageSettings {
      ...RegistrationPageSettings
    }
    seriesSettings {
      ...SeriesSettings
    }
    registrationFormSettings {
      ...RegistrationFormSettings
    }
    submitEmailFormSettings {
      ...SubmitEmailFormSettings
    }
    themeSettings {
      ...ThemeSettings
    }
    notificationSettings {
      ...NotificationSettings
    }
  }
  ${REGISTRATION_PAGE_SETTINGS_FRAGMENT}
  ${SERIES_SETTINGS_FRAGMENT}
  ${REGISTRATION_FORM_SETTING_FRAGMENT}
  ${SUBMIT_EMAIL_FORM_SETTINGS_FRAGMENT}
  ${THEME_SETTINGS_FRAGMENT}
  ${NOTIFICATION_SETTINGS_FRAGMENT}
  ${MEDIAREF}
  ${SCRIPT_IN_SERIES_FRAGMENT}
  ${SET_PREVIEW_INFO}
  ${TEAM_SETTINGS_FRAGMENT}
`;

export const PUBLIC_SERIES_FRAGMENT = gql`
  fragment PublicSeries on Series {
    id
    name
    language
    tag
    url
    updatedAt
    team {
      id
      name
      shareUrl
      settings {
        ...TeamSettings
      }
    }
    logoMediaRef {
      ...MediaRef
    }
    favIconMediaRef {
      ...MediaRef
    }
    sets {
      ...PublicSet
    }
    registrationPageSettings {
      ...RegistrationPageSettings
    }
    seriesSettings {
      ...SeriesSettings
    }
    registrationFormSettings {
      ...RegistrationFormSettings
    }
    submitEmailFormSettings {
      ...SubmitEmailFormSettings
    }
    themeSettings {
      ...ThemeSettings
    }
  }
  ${REGISTRATION_PAGE_SETTINGS_FRAGMENT}
  ${SERIES_SETTINGS_FRAGMENT}
  ${REGISTRATION_FORM_SETTING_FRAGMENT}
  ${SUBMIT_EMAIL_FORM_SETTINGS_FRAGMENT}
  ${THEME_SETTINGS_FRAGMENT}
  ${MEDIAREF}
  ${PUBLIC_SET_FRAGMENT}
  ${TEAM_SETTINGS_FRAGMENT}
`;

export const ALL_SERIES = gql`
  query allSeries {
    allSeries {
      ...Series
    }
  }
  ${SERIES_FRAGMENT}
`;

export const GET_SERIES = gql`
  query series($id: String!) {
    series(id: $id) {
      ...FullSeries
    }
  }
  ${FULL_SERIES_FRAGMENT}
`;

export const GET_PUBLIC_SERIES = gql`
  query publicSeries($id: String!) {
    series(id: $id) {
      ...PublicSeries
    }
  }
  ${PUBLIC_SERIES_FRAGMENT}
`;

export const CREATE_SERIES = gql`
  mutation createSeries($data: SeriesInput!) {
    createSeries(data: $data) {
      id
    }
  }
  ${SERIES_FRAGMENT}
`;

export const UPDATE_SERIES = gql`
  mutation updateSeries($id: String!, $data: SeriesInput!) {
    updateSeries(id: $id, data: $data) {
      ...FullSeries
    }
  }
  ${FULL_SERIES_FRAGMENT}
`;

export const DELETE_SERIES = gql`
  mutation deleteSeries($id: String!) {
    deleteSeries(id: $id)
  }
`;

export const RENAME_SERIES_TAG = gql`
  mutation renameSeriesTag($id: String!, $newTag: String!) {
    renameSeriesTag(id: $id, newTag: $newTag) {
      ...Series
    }
  }
  ${SERIES_FRAGMENT}
`;
